import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Flame, Car, Factory, Wind, Zap, Thermometer, ChevronDown, ChevronRight } from 'lucide-react';
import { getStyles, ENERGY_CATEGORIES } from './wizardConstants';
import FactorPickerInline from './FactorPickerInline';
import { useLanguage } from '../../context/LanguageContext';

const ICONS = { Flame, Car, Factory, Wind, Zap, Thermometer };

const CategoryLabel = ({ cat, isDark, language, categories }) => {
  const Icon = ICONS[cat.icon] || Zap;
  const catDoc = categories?.find(c => c.code === cat.code);
  const label = catDoc ? (language === 'de' ? (catDoc.name_de || catDoc.name_fr) : catDoc.name_fr) : cat.code;
  return (
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${cat.color}`} />
      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</span>
    </div>
  );
};


const EnergyEntriesStep = ({
  phase,
  title,
  subtitle,
  formData,
  energyFactors,
  addEntry,
  updateEntry,
  removeEntry,
  setFormData,
  isDark,
  language,
  categories,
  showCycles = false,
}) => {
  const s = getStyles(isDark);
  const { t } = useLanguage();
  const phaseData = formData[phase] || { entries: [] };
  const entries = phaseData.entries || [];
  const [expandedCat, setExpandedCat] = useState(null);
  const [autoOpenCategory, setAutoOpenCategory] = useState(null);

  const toggleCat = (code) => {
    const isOpening = expandedCat !== code;
    setExpandedCat(prev => prev === code ? null : code);
    if (isOpening && (entriesByCat[code] || []).length === 0) {
      addEntry(phase, code);
      setAutoOpenCategory(code);
    }
  };

  // Group entries by category
  const entriesByCat = {};
  entries.forEach((e, i) => {
    if (!entriesByCat[e.category]) entriesByCat[e.category] = [];
    entriesByCat[e.category].push({ ...e, _index: i });
  });

  const getFactorUnit = (entry) => {
    if (!entry.factor_id) return '';
    const factors = energyFactors[entry.category] || [];
    const f = factors.find(x => x.id === entry.factor_id);
    return f?.default_unit || f?.unit || '';
  };

  return (
    <motion.div key={phase} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
      <div>
        <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{subtitle}</p>
      </div>

      {showCycles && (
        <div className={`p-4 rounded-xl ${isDark ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className={`block text-xs mb-1 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>{t('productWizard.usage.cyclesPerYear')}</label>
              <input type="text" inputMode="numeric"
                value={phaseData.cycles_per_year === 0 ? '' : phaseData.cycles_per_year}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^[0-9]+$/.test(v)) {
                    setFormData(prev => ({ ...prev, [phase]: { ...prev[phase], cycles_per_year: v === '' ? 1 : parseInt(v) || 1 } }));
                  }
                }}
                className={s.input} placeholder="1" data-testid="usage-cycles-input" />
            </div>
            <div className={`text-center px-4 py-2 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-white'}`}>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{t('productWizard.usage.totalCyclesOverLife')}</p>
              <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="usage-total-cycles">
                {Math.round(formData.lifespan_years * (phaseData.cycles_per_year || 1))}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Category accordion */}
      <div className="space-y-2">
        {ENERGY_CATEGORIES.map(cat => {
          const catEntries = entriesByCat[cat.code] || [];
          const isExpanded = expandedCat === cat.code;
          const factorsAvailable = (energyFactors[cat.code] || []).length;

          return (
            <div key={cat.code} className={`rounded-xl border overflow-hidden transition-colors ${
              isDark ? 'border-slate-600 bg-slate-700/50' : 'border-gray-200 bg-white'
            }`}>
              {/* Category header */}
              <button
                onClick={() => toggleCat(cat.code)}
                className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                  isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-50'
                }`}
                data-testid={`cat-header-${cat.code}`}
              >
                <CategoryLabel cat={cat} isDark={isDark} language={language} categories={categories} />
                <div className="flex items-center gap-3">
                  {catEntries.length > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600'
                    }`}>{t(catEntries.length === 1 ? 'productWizard.energy.entriesCount_one' : 'productWizard.energy.entriesCount_other').replace('{n}', catEntries.length)}</span>
                  )}
                  {factorsAvailable === 0 && (
                    <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{t('productWizard.energy.noFactor')}</span>
                  )}
                  {isExpanded ? <ChevronDown className="w-4 h-4 opacity-50" /> : <ChevronRight className="w-4 h-4 opacity-50" />}
                </div>
              </button>

              {/* Expanded: entries + add button */}
              {isExpanded && (
                <div className={`px-4 pb-4 pt-1 space-y-3 border-t ${isDark ? 'border-slate-600' : 'border-gray-100'}`}>
                  {catEntries.map((entry) => (
                    <div key={entry._key || entry._index} className={`p-3 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-1 space-y-2">
                          <FactorPickerInline
                            factors={energyFactors[cat.code] || []}
                            categoryCode={cat.code}
                            selectedFactorId={entry.factor_id}
                            autoOpen={autoOpenCategory === cat.code && entry._index === catEntries.at(-1)._index}
                            onSelectFactor={(f) => {
                              updateEntry(phase, entry._index, 'factor_id', f.id);
                              updateEntry(phase, entry._index, 'unit', f.default_unit || f.unit || '');
                              setAutoOpenCategory(null);
                            }}
                            label={t('productWizard.energy.factorLabel')}
                          />
                          <div>
                            <label className={s.label}>
                              {showCycles ? t('productWizard.energy.quantityPerCycle') : t('productWizard.energy.quantityPerUnit')}{entry.unit ? ` (${entry.unit})` : ''}
                            </label>
                            <input type="text" inputMode="decimal"
                              value={entry.quantity === 0 ? '' : (entry.quantity ?? '')}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '' || /^[0-9]*[.,]?[0-9]*$/.test(v)) {
                                  updateEntry(phase, entry._index, 'quantity', v);
                                }
                              }}
                              className={s.input} placeholder="0" data-testid={`entry-qty-${entry._index}`} />
                          </div>
                        </div>
                        <button onClick={() => removeEntry(phase, entry._index)}
                          className="p-2 rounded-lg hover:bg-red-500/20 text-red-500 mt-1 flex-shrink-0"
                          data-testid={`entry-remove-${entry._index}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {factorsAvailable > 0 && (
                    <button onClick={() => addEntry(phase, cat.code)}
                      className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg border-2 border-dashed text-sm font-medium transition-colors ${
                        isDark ? 'border-slate-600 text-slate-400 hover:border-blue-500/50 hover:text-blue-400'
                          : 'border-gray-200 text-gray-400 hover:border-blue-400 hover:text-blue-500'
                      }`}
                      data-testid={`add-entry-${cat.code}`}>
                      <Plus className="w-4 h-4" />
                      {t('productWizard.energy.addEntry')}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default EnergyEntriesStep;
